import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { ApiService } from '../services/api';
import { Question } from '../types';
import TimerWrapper from '../components/TimerWrapper';
import { StudentSessionStats } from '../components/SessionStatistics';
import { marked } from 'marked';

const StudentSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { socket, isConnected, error: socketError } = useSocket();
  
  const [studentInfo, setStudentInfo] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [answerSubmitted, setAnswerSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timerExpired, setTimerExpired] = useState<boolean>(false);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [showStatistics, setShowStatistics] = useState(false);

  // Fonction pour déconnecter l'étudiant et nettoyer la session
  const handleDisconnect = () => {
    if (socket) {
      socket.leaveSession();
    }
    localStorage.removeItem('studentInfo');
    navigate('/student/join');
  };

  // Fonction appelée quand le timer expire
  const handleTimerExpired = useCallback(async () => {
    setTimerExpired(true);
    console.log('⏰ Timer expiré pour la question:', currentQuestion?.title);
    
    // Envoyer automatiquement une réponse générique pour comptabiliser
    if (currentQuestion && studentInfo?.id && !answerSubmitted) {
      console.log('🚀 Auto-soumission d\'une réponse générique à l\'expiration du timer');
      
      let autoResponse = '';
      
      // Toujours utiliser "Pas de réponse" pour éviter de fausser les statistiques
      autoResponse = '[Pas de réponse - Temps écoulé]';
      
      try {
        // Soumettre la réponse générique
        const response = await ApiService.submitAnswer(
          currentQuestion.id,
          studentInfo.id,
          studentInfo.name,
          autoResponse
        );
        
        if (response.success) {
          console.log('✅ Réponse générique soumise avec succès');
          setAnswerSubmitted(true);
          setSelectedAnswer(autoResponse);
          
          // Afficher une notification à l'étudiant
          setError('⏰ Temps écoulé ! Une réponse générique a été soumise automatiquement.');
          
          // Nettoyer le message d'erreur après 5 secondes
          setTimeout(() => setError(null), 5000);
        } else {
          console.error('❌ Erreur lors de la soumission de la réponse générique:', response.message);
        }
      } catch (err: any) {
        console.error('❌ Erreur lors de l\'auto-soumission:', err);
      }
    }
  }, [currentQuestion, studentInfo, answerSubmitted]);

  // Réinitialiser le timer quand une nouvelle question arrive
  useEffect(() => {
    if (currentQuestion) {
      setTimerExpired(false);
    }
  }, [currentQuestion]);

  // Fonction pour vérifier si l'étudiant a déjà répondu
  const checkIfAlreadyAnswered = useCallback((question: any, studentId: string) => {
    if (!question?.responses || !studentId) return false;
    
    const hasAnswered = question.responses.find(
      (response: any) => response.studentId === studentId
    );
    
    if (hasAnswered) {
      setAnswerSubmitted(true);
      setSelectedAnswer(hasAnswered.answer);
      console.log('✅ Étudiant a déjà répondu à cette question:', hasAnswered);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    // Récupérer les infos de l'étudiant depuis localStorage
    const storedStudentInfo = localStorage.getItem('studentInfo');
    if (!storedStudentInfo) {
      navigate('/student/join');
      return;
    }

    const student = JSON.parse(storedStudentInfo);
    setStudentInfo(student);

    // Charger la session
    const loadSession = async () => {
      try {
        setLoading(true);
        const response = await ApiService.getSession(student.sessionId);
        
        if (response.success) {
          setSession(response.session);
          setAllQuestions(response.session.questions || []);
          
          // Si une question est active, la récupérer
          if (response.session.currentQuestion) {
            setCurrentQuestion(response.session.currentQuestion);
            console.log('🔔 Question activée reçue:', response.session.currentQuestion?.title);
            
            // Vérifier si l'étudiant a déjà répondu à cette question
            checkIfAlreadyAnswered(response.session.currentQuestion, student.id);
          }
        }
      } catch (err: any) {
        setError(err.message || 'Erreur lors du chargement de la session');
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId, navigate, checkIfAlreadyAnswered]);

  useEffect(() => {
    if (studentInfo && socket) {
      // Rejoindre la session via socket
      socket.joinSession(
        studentInfo.sessionCode,
        'student',
        studentInfo.id,
        studentInfo.name
      );

      // Écouter les événements socket
      socket.on('session-joined', (data) => {
        console.log('Session rejointe:', data);
      });

      socket.on('question-activated', (data) => {
        console.log('🔔 Question activée reçue:', data.sessionCode);
        console.log('🔔 Question object:', data.question?.title);
        console.log('🔔 Question ID:', data.question?.id);
        console.log('🔔 QuestionId from notification:', data.questionId);
        
        // Utiliser questionId de la notification si l'objet question n'a pas d'id
        const questionToSet = data.question;
        if (questionToSet && !questionToSet.id && data.questionId) {
          questionToSet.id = data.questionId;
        }
        
        setCurrentQuestion(questionToSet);
        console.log('🔔 Question activée via WebSocket:', questionToSet.title);
        
        // Vérifier si l'étudiant a déjà répondu à cette question
        if (!checkIfAlreadyAnswered(questionToSet, studentInfo.id)) {
          setAnswerSubmitted(false);
          setSelectedAnswer('');
        }
      });

      socket.on('answer-confirmed', (data) => {
        setAnswerSubmitted(true);
      });

      socket.on('session-stopped', () => {
        navigate('/student/join', { 
          state: { message: 'La session a été terminée par l\'enseignant' }
        });
      });

      socket.on('session-status-changed', (data) => {
        console.log('📢 Statut de session changé:', data.sessionId);
        // Mettre à jour le statut de session local
        setSession((prevSession: any) => {
          if (!prevSession) return prevSession;
          return {
            ...prevSession,
            status: data.status
          };
        });
        
        // Actions spécifiques selon le nouveau statut
        if (data.status === 'ended') {
          navigate('/student/join', { 
            state: { message: 'La session a été terminée par l\'enseignant' }
          });
        } else if (data.status === 'paused') {
          setError('⏸️ La session a été mise en pause par l\'enseignant. Le formulaire est temporairement désactivé.');
        } else if (data.status === 'active') {
          setError(null); // Effacer les messages d'erreur si la session redevient active
        }
      });

      return () => {
        socket.leaveSession();
      };
    }
  }, [studentInfo, socket, navigate, checkIfAlreadyAnswered]);

  const submitAnswer = async () => {
    if (!currentQuestion || !selectedAnswer.trim()) return;
    
    // Vérifier que la session est active
    if (!session || session.status !== 'active') {
      if (session?.status === 'paused') {
        setError('⏸️ Impossible de soumettre une réponse : la session est en pause.');
      } else if (session?.status === 'ended') {
        setError('🔴 Impossible de soumettre une réponse : la session est terminée.');
      } else {
        setError('⚠️ Impossible de soumettre une réponse : la session n\'est pas active.');
      }
      return;
    }

    console.log('📤 Envoi de réponse:', {
      questionId: currentQuestion.id,
      studentId: studentInfo.id,
      studentName: studentInfo.name,
      answer: selectedAnswer
    });

    try {
      await socket?.submitAnswer(
        currentQuestion.id,
        studentInfo.id,
        studentInfo.name,
        selectedAnswer
      );
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la soumission de la réponse');
    }
  };

  const handleAnswerChange = (answer: string) => {
    // Empêcher la modification après soumission
    if (answerSubmitted) return;
    setSelectedAnswer(answer);
  };

  const getQuestionTypeDisplay = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return 'Question à choix multiples';
      case 'investigation':
        return "Question d'investigation";
      case 'true_false':
        return 'Vrai/Faux';
      case 'open_ended':
        return 'Question ouverte';
      case 'rating':
        return 'Évaluation';
      default:
        return 'Question';
    }
  };

  // Fonction pour vérifier si la réponse est correcte
  const isAnswerCorrect = (question: Question, selectedAnswer: string): boolean => {
    if (!question.correctAnswer || !selectedAnswer) return false;

    if (question.type === 'investigation') {
      return false;
    }
    
    if (question.type === 'multiple_choice' || question.type === 'true_false') {
      return selectedAnswer === question.correctAnswer;
    }
    
    return false;
  };


  // Fonction pour obtenir l'icône de feedback
  const getAnswerFeedbackIcon = (question: Question, selectedAnswer: string, isSubmitted: boolean) => {
    if (!isSubmitted || !question.correctAnswer) {
      return '';
    }
    if (question.type === 'investigation') {
      return '';
    }

    const isCorrect = isAnswerCorrect(question, selectedAnswer);
    return isCorrect ? '✅' : '❌';
  };

  if (loading) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Session</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="loading">
            <div className="spinner"></div>
            <p className="mt-2">Connexion à la session...</p>
          </div>
        </main>
      </div>
    );
  }

  // Ne plus rediriger en cas d'erreur, on affichera l'erreur dans l'interface normale

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">{session?.name || 'Session'}</h1>
          <div className="header-actions">
            <button 
              onClick={() => navigate('/student/join')} 
              className="btn btn-outline btn-sm"
            >
              ← Retour
            </button>
            <button 
              onClick={handleDisconnect} 
              className="btn btn-outline btn-sm"
              style={{ 
                background: '#dc3545', 
                borderColor: '#dc3545', 
                color: 'white' 
              }}
              title="Se déconnecter et effacer la session"
            >
              🚪 Déconnexion
            </button>
            {session?.status === 'ended' && (
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="btn btn-info btn-sm"
              >
                {showStatistics ? '📊 Masquer Stats' : '📊 Voir Stats'}
              </button>
            )}
            <div className={`status-badge ${isConnected ? 'status-active' : 'status-created'}`}>
              {isConnected ? '🟢 Connecté' : '🔴 Déconnecté'}
            </div>
            <div className={`status-badge ${session?.status === 'active' ? 'status-active' : session?.status === 'paused' ? 'status-created' : 'status-created'}`}>
              {session?.status === 'active' ? '📚 Session Active' : 
               session?.status === 'paused' ? '⏸️ Session Pause' : 
               session?.status === 'ended' ? '🔴 Session Terminée' : 
               '⏳ Session En Attente'}
            </div>
            <span className="text-white">| {studentInfo?.name}</span>
          </div>
        </div>
      </header>

      <main className="main-content">
        {socketError && (
          <div className="mb-3 p-2" style={{ 
            background: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            ❌ {socketError}
          </div>
        )}

        {error && (
          <div className="mb-3 p-2" style={{ 
            background: '#fff3cd', 
            color: '#856404', 
            borderRadius: '4px',
            border: '1px solid #ffeaa7'
          }}>
            ⚠️ {error}
          </div>
        )}

        {!currentQuestion ? (
          <div className="card text-center">
            <div className="card-content">
              <div className="loading">
                <div className="spinner"></div>
              </div>
              <h3 className="mt-3">En attente de question</h3>
              <p>
                Votre enseignant va bientôt activer une question. 
                Restez connecté !
              </p>
              <div className="mt-3">
                <div className="status-badge status-active">
                  Session: {session?.status || 'En cours'}
                </div>
              </div>
            </div>
          </div>
        ) : !showStatistics && (
          <div className="card fade-in">
            <div className="card-header">
              <h2 className="card-title">{currentQuestion.title}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span className="status-badge status-active">
                  {getQuestionTypeDisplay(currentQuestion.type)}
                </span>
                {!session.disableTimer && (
                  <TimerWrapper 
                    timeLimit={currentQuestion.timeLimit || 60}
                    onTimeUp={handleTimerExpired}
                    isActive={!answerSubmitted && !timerExpired && session && session.status === 'active'}
                    questionId={currentQuestion.id}
                  />
                )}
              </div>
            </div>
            
            <div className="card-content">
              {/* Message d'avertissement si le timer expire */}
              {timerExpired && (
                <div className="mb-3 p-3" style={{ 
                  background: '#fff3cd', 
                  color: '#856404', 
                  borderRadius: '8px',
                  border: '1px solid #ffeaa7',
                  textAlign: 'center'
                }}>
                  <strong>⏰ Temps écoulé !</strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    {answerSubmitted ? 
                      'Une réponse générique a été soumise automatiquement pour comptabiliser votre participation.' :
                      'Le temps pour répondre à cette question est terminé.'
                    }
                  </p>
                </div>
              )}

              {/* Message informatif si la session est en pause ou terminée */}
              {session && session.status !== 'active' && (
                <div className="mb-3 p-3" style={{ 
                  background: session.status === 'paused' ? '#fff3cd' : session.status === 'ended' ? '#f8d7da' : '#fff3cd', 
                  color: session.status === 'paused' ? '#856404' : session.status === 'ended' ? '#721c24' : '#856404', 
                  borderRadius: '8px',
                  border: session.status === 'paused' ? '1px solid #ffeaa7' : session.status === 'ended' ? '1px solid #f5c6cb' : '1px solid #ffeaa7',
                  textAlign: 'center'
                }}>
                  <strong>
                    {session.status === 'paused' ? '⏸️ Session en pause' : session.status === 'ended' ? '🔴 Session terminée' : '🟢 Session non démarrée'}
                  </strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    {session.status === 'paused' ? 
                      'Le formulaire est temporairement désactivé. Attendez que l\'enseignant reprenne la session.' :
                      (session.status === 'ended' ? 'La session est terminée. Vous ne pouvez plus répondre aux questions.' :
                      'La session n\'est pas encore démarrée. Attendez que l\'enseignant démarre la session.')
                    }
                  </p>
                </div>
              )}
              
              <div className="mb-3">
                <h4>Question:</h4>
                <p>{currentQuestion.content}</p>
              </div>

              {['multiple_choice', 'investigation'].includes(currentQuestion.type) && (
                <div>
                  <h4>Choisissez votre réponse:</h4>
                  <div className="mt-2">
                    {currentQuestion.options.map((option) => {
                      const isSelected = selectedAnswer === option.id;
                      const isCorrectAnswer = currentQuestion.correctAnswer === option.id;
                      const isSubmitted = answerSubmitted;
                      
                      // Style de base
                      let optionStyle: any = {
                        cursor: (!session || session.status !== 'active' || answerSubmitted) ? 'not-allowed' : 'pointer',
                        borderRadius: '8px',
                        opacity: (!session || session.status !== 'active' || answerSubmitted) ? 0.6 : 1,
                        transition: 'all 0.3s ease',
                        marginBottom: '0.75rem',
                        padding: '1rem',
                        position: 'relative'
                      };

                      // Style selon l'état
                      if (isSubmitted && currentQuestion.correctAnswer && currentQuestion.type !== 'investigation') {
                        if (isSelected && isCorrectAnswer) {
                          // Réponse correcte sélectionnée
                          optionStyle = {
                            ...optionStyle,
                            background: '#d4edda',
                            border: '2px solid #28a745',
                            color: '#155724'
                          };
                        } else if (isSelected && !isCorrectAnswer) {
                          // Mauvaise réponse sélectionnée
                          optionStyle = {
                            ...optionStyle,
                            background: '#f8d7da',
                            border: '2px solid #dc3545',
                            color: '#721c24'
                          };
                        } else if (!isSelected && isCorrectAnswer) {
                          // Bonne réponse non sélectionnée (à mettre en évidence)
                          optionStyle = {
                            ...optionStyle,
                            background: '#fff3cd',
                            border: '2px solid #ffc107',
                            color: '#856404'
                          };
                        } else {
                          // Autres options
                          optionStyle = {
                            ...optionStyle,
                            background: '#f8f9fa',
                            border: '2px solid #e9ecef',
                            color: '#6c757d'
                          };
                        }
                      } else {
                        // Style normal (pas encore soumis)
                        optionStyle = {
                          ...optionStyle,
                          background: isSelected ? '#e3f2fd' : '#f8f9fa',
                          border: isSelected ? '2px solid #2196f3' : '2px solid transparent',
                          color: '#495057'
                        };
                      }

                      return (
                        <div 
                          key={option.id} 
                          className="option-container"
                          style={optionStyle}
                          onClick={() => {
                            if (session && session.status === 'active' && !timerExpired && !answerSubmitted) {
                              handleAnswerChange(option.id);
                            }
                          }}
                        >
                          <label className="flex align-center gap-2" style={{ cursor: 'inherit', margin: 0 }}>
                            <input
                              type="radio"
                              name="answer"
                              value={option.id}
                              checked={selectedAnswer === option.id}
                              onChange={(e) => handleAnswerChange(e.target.value)}
                              disabled={!session || session.status !== 'active' || timerExpired || answerSubmitted}
                              style={{ margin: 0 }}
                            />
                            <span style={{ fontWeight: '500', fontSize: '1rem' }}>
                              {option.text}
                               {isSubmitted && currentQuestion.correctAnswer && currentQuestion.type !== 'investigation' && (
                                <span style={{ marginLeft: '0.5rem' }}>
                                  {isSelected && isCorrectAnswer && '✅'}
                                  {isSelected && !isCorrectAnswer && '❌'}
                                  {!isSelected && isCorrectAnswer && '✓'}
                                </span>
                              )}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {currentQuestion.type === 'true_false' && (
                <div>
                  <h4>Choisissez votre réponse:</h4>
                  <div className="mt-2">
                    {currentQuestion.options.map((option) => {
                      const isSelected = selectedAnswer === option.id;
                      const isCorrectAnswer = currentQuestion.correctAnswer === option.id;
                      const isSubmitted = answerSubmitted;
                      
                      // Style de base
                      let optionStyle: any = {
                        cursor: (!session || session.status !== 'active' || answerSubmitted) ? 'not-allowed' : 'pointer',
                        borderRadius: '8px',
                        opacity: (!session || session.status !== 'active' || answerSubmitted) ? 0.6 : 1,
                        transition: 'all 0.3s ease',
                        marginBottom: '0.75rem',
                        padding: '1rem',
                        position: 'relative'
                      };

                      // Style selon l'état
                      if (isSubmitted && currentQuestion.correctAnswer) {
                        if (isSelected && isCorrectAnswer) {
                          // Réponse correcte sélectionnée
                          optionStyle = {
                            ...optionStyle,
                            background: '#d4edda',
                            border: '2px solid #28a745',
                            color: '#155724'
                          };
                        } else if (isSelected && !isCorrectAnswer) {
                          // Mauvaise réponse sélectionnée
                          optionStyle = {
                            ...optionStyle,
                            background: '#f8d7da',
                            border: '2px solid #dc3545',
                            color: '#721c24'
                          };
                        } else if (!isSelected && isCorrectAnswer) {
                          // Bonne réponse non sélectionnée (à mettre en évidence)
                          optionStyle = {
                            ...optionStyle,
                            background: '#fff3cd',
                            border: '2px solid #ffc107',
                            color: '#856404'
                          };
                        } else {
                          // Autres options
                          optionStyle = {
                            ...optionStyle,
                            background: '#f8f9fa',
                            border: '2px solid #e9ecef',
                            color: '#6c757d'
                          };
                        }
                      } else {
                        // Style normal (pas encore soumis)
                        optionStyle = {
                          ...optionStyle,
                          background: isSelected ? '#e3f2fd' : '#f8f9fa',
                          border: isSelected ? '2px solid #2196f3' : '2px solid transparent',
                          color: '#495057'
                        };
                      }

                      return (
                        <div 
                          key={option.id} 
                          className="option-container"
                          style={optionStyle}
                          onClick={() => {
                            if (session && session.status === 'active' && !timerExpired && !answerSubmitted) {
                              handleAnswerChange(option.id);
                            }
                          }}
                        >
                          <label className="flex align-center gap-2" style={{ cursor: 'inherit', margin: 0 }}>
                            <input
                              type="radio"
                              name="answer"
                              value={option.id}
                              checked={selectedAnswer === option.id}
                              onChange={(e) => handleAnswerChange(e.target.value)}
                              disabled={!session || session.status !== 'active' || timerExpired || answerSubmitted}
                              style={{ margin: 0 }}
                            />
                            <span style={{ fontWeight: '500', fontSize: '1rem' }}>
                              {option.text}
                              {isSubmitted && currentQuestion.correctAnswer && (
                                <span style={{ marginLeft: '0.5rem' }}>
                                  {isSelected && isCorrectAnswer && '✅'}
                                  {isSelected && !isCorrectAnswer && '❌'}
                                  {!isSelected && isCorrectAnswer && '✓'}
                                </span>
                              )}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Affichage de la réponse soumise */}
              {answerSubmitted && (
                <div className="mb-3 p-3" style={{ 
                  background: timerExpired ? '#fff3cd' : 
                            (currentQuestion.correctAnswer && isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#d4edda' :
                            (currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#f8d7da' : '#d4edda', 
                  color: timerExpired ? '#856404' : 
                        (currentQuestion.correctAnswer && isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#155724' :
                        (currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#721c24' : '#155724', 
                  borderRadius: '8px',
                  border: `1px solid ${timerExpired ? '#ffeaa7' : 
                            (currentQuestion.correctAnswer && isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#c3e6cb' :
                            (currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#f5c6cb' : '#c3e6cb'}`
                }}>
                  <h5>
                    {getAnswerFeedbackIcon(currentQuestion, selectedAnswer, answerSubmitted)} Votre réponse:
                    {timerExpired && <span style={{ fontSize: '0.8em', marginLeft: '0.5rem' }}>(Auto-soumise)</span>}
                    {!timerExpired && currentQuestion.correctAnswer && (
                      <span style={{ fontSize: '0.8em', marginLeft: '0.5rem' }}>
                        {isAnswerCorrect(currentQuestion, selectedAnswer) ? '(Correcte!)' : '(Incorrecte)'}
                      </span>
                    )}
                  </h5>
                  {currentQuestion.type === 'open_ended' ? (
                    <p style={{ margin: 0, fontStyle: 'italic' }}>
                      {selectedAnswer}
                      {timerExpired && selectedAnswer === '[Pas de réponse - Temps écoulé]' && (
                        <span style={{ fontSize: '0.8em', color: '#6c757d', marginLeft: '0.5rem' }}>
                          ⏰ (Générée automatiquement)
                        </span>
                      )}
                    </p>
                  ) : currentQuestion.type === 'rating' ? (
                    <div className="rating-display">
                      <span className="rating-badge" style={{
                        padding: '0.5rem 1rem',
                        background: timerExpired ? '#fff3cd' : '#d4edda',
                        color: timerExpired ? '#856404' : '#155724',
                        border: `2px solid ${timerExpired ? '#ffeaa7' : '#c3e6cb'}`,
                        borderRadius: '8px',
                        fontWeight: 'bold'
                      }}>
                        {currentQuestion.ratingScale?.labels?.[parseInt(selectedAnswer) - currentQuestion.ratingScale.min] || selectedAnswer}
                        {timerExpired && selectedAnswer === '[Pas de réponse - Temps écoulé]' && (
                          <span style={{ fontSize: '0.8em', marginLeft: '0.5rem' }}>
                            ⏰ (Générée automatiquement)
                          </span>
                        )}
                      </span>
                      {/* Affichage de l'explication pour les questions rating si disponible */}
                      {currentQuestion.ratingScale?.labels && 
                       currentQuestion.ratingScale.labels[parseInt(selectedAnswer) - currentQuestion.ratingScale.min] && (
                        <div 
                          className="explication-display"
                          style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '8px',
                            borderLeft: '4px solid #2196f3',
                            fontSize: '0.9rem',
                            lineHeight: '1.5',
                            color: '#1976d2'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <i className="fas fa-info-circle" style={{ color: '#2196f3' }}></i>
                            <strong>Note :</strong>
                          </div>
                          <p style={{ margin: 0 }}>
                            Vous avez donné une note de {selectedAnswer} sur {currentQuestion.ratingScale.max}.
                            {currentQuestion.ratingScale.labels[parseInt(selectedAnswer) - currentQuestion.ratingScale.min] && 
                             ` Cela correspond à : "${currentQuestion.ratingScale.labels[parseInt(selectedAnswer) - currentQuestion.ratingScale.min]}".`}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <span className={`answer-badge ${timerExpired ? 'warning' : 'correct'}`}>
                        {currentQuestion.options?.find(opt => opt.id === selectedAnswer)?.text || 
                          `Option ${selectedAnswer}`
                        }
                        {timerExpired && (
                          <span style={{ fontSize: '0.7em', marginLeft: '0.5rem' }}>
                            ⏰ (Auto-sélectionnée)
                          </span>
                        )}
                      </span>
                      {/* Affichage de l'explication après validation */}
                      {(() => {
                        const selectedOption = currentQuestion.options?.find(opt => opt.id === selectedAnswer);
                        return selectedOption && selectedOption.explication;
                      })() && (
                        <div 
                          className="explication-display"
                          style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '8px',
                            borderLeft: '4px solid #2196f3',
                            fontSize: '0.9rem',
                            lineHeight: '1.5',
                            color: '#1976d2'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <i className="fas fa-info-circle" style={{ color: '#2196f3' }}></i>
                            <strong>Explication :</strong>
                          </div>
                          <p style={{ margin: 0 }}
                            dangerouslySetInnerHTML={{ __html: marked.parse(currentQuestion.options?.find(opt => opt.id === selectedAnswer)?.explication || '', {
                              breaks: true,
                              gfm: true
                            }) as string }}/>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Affichage de la bonne réponse si la réponse est incorrecte */}
                   {!timerExpired && currentQuestion.type !== 'investigation' && currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer) && (
                    <div 
                      className="correct-answer-display"
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#e8f5e8',
                        borderRadius: '8px',
                        border: '1px solid #4caf50',
                        color: '#2e7d32'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <i className="fas fa-check-circle" style={{ color: '#4caf50' }}></i>
                        <strong>Bonne réponse :</strong>
                      </div>
                      <p style={{ margin: 0, fontWeight: '500' }}>
                        {currentQuestion.options?.find(opt => opt.id === currentQuestion.correctAnswer)?.text || 
                         `Option ${currentQuestion.correctAnswer}`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {currentQuestion.type === 'open_ended' && !answerSubmitted && (
                <div>
                  <h4>Votre réponse:</h4>
                  <textarea
                    className="form-input form-textarea mt-2"
                    placeholder="Saisissez votre réponse ici..."
                    value={selectedAnswer}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    disabled={timerExpired}
                    style={{ opacity: 1 }}
                  />
                </div>
              )}

              {currentQuestion.type === 'rating' && (
                <div>
                  <h4>Votre évaluation:</h4>
                  <div className="rating-container mt-2">
                    {currentQuestion.ratingScale ? (
                      <div className="rating-scale">
                        <div className="rating-labels mb-2">
                          <span className="rating-min">
                            {currentQuestion.ratingScale.labels?.[0] || currentQuestion.ratingScale.min}
                          </span>
                          <span className="rating-max">
                            {currentQuestion.ratingScale.labels?.[currentQuestion.ratingScale.max - 1] || currentQuestion.ratingScale.max}
                          </span>
                        </div>
                        <div className="rating-buttons">
                          {Array.from({ length: currentQuestion.ratingScale.max - currentQuestion.ratingScale.min + 1 }, (_, index) => {
                            const value = currentQuestion.ratingScale!.min + index;
                            const label = currentQuestion.ratingScale!.labels?.[index] || value.toString();
                            return (
                              <label key={value} className="rating-option" style={{ margin: '0.25rem' }}>
                                <input
                                  type="radio"
                                  name="rating"
                                  value={value.toString()}
                                  checked={selectedAnswer === value.toString()}
                                  onChange={(e) => handleAnswerChange(e.target.value)}
                                  disabled={!session || session.status !== 'active' || timerExpired || answerSubmitted}
                                  style={{ marginRight: '0.5rem' }}
                                />
                                <span className="rating-value" style={{
                                  padding: '0.5rem 1rem',
                                  border: '2px solid #e9ecef',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  background: selectedAnswer === value.toString() ? '#007bff' : 'white',
                                  color: selectedAnswer === value.toString() ? 'white' : '#495057',
                                  transition: 'all 0.2s ease',
                                  display: 'inline-block',
                                  minWidth: '60px',
                                  textAlign: 'center'
                                }}>
                                  {label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '1rem', 
                        background: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        borderRadius: '8px',
                        color: '#856404'
                      }}>
                        <strong>⚠️ Erreur d'affichage:</strong> Les données de l'échelle de notation ne sont pas disponibles.
                        <br />
                        <small>Veuillez contacter l'enseignant ou recharger la page.</small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="card-actions">
              {answerSubmitted ? (
                <div className="flex align-center gap-2" style={{ 
                  color: timerExpired ? '#856404' : 
                        (currentQuestion.correctAnswer && isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#28a745' :
                        (currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer)) ? '#dc3545' : 'var(--success-color)' 
                }}>
                  <span>
                    {timerExpired ? '⏰' : 
                     (currentQuestion.correctAnswer && isAnswerCorrect(currentQuestion, selectedAnswer)) ? '✅' :
                     (currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer)) ? '❌' : '✅'}
                  </span>
                  <span>
                    {timerExpired ? 
                      'Réponse générique soumise automatiquement (temps écoulé)' : 
                      (currentQuestion.type !== 'investigation' && currentQuestion.correctAnswer && isAnswerCorrect(currentQuestion, selectedAnswer)) ? 
                        'Réponse correcte ! 🎉' :
                       (currentQuestion.type !== 'investigation' && currentQuestion.correctAnswer && !isAnswerCorrect(currentQuestion, selectedAnswer)) ? 
                        'Réponse incorrecte. Essayez de mieux comprendre la question.' :
                        'Réponse soumise avec succès !'
                    }
                  </span>
                </div>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={submitAnswer}
                  disabled={!selectedAnswer.trim() || !session || session.status !== 'active' || timerExpired}
                >
                  {timerExpired ? '⏰ Temps écoulé' : '📤 Soumettre ma réponse'}
                </button>
              )}
              
            </div>

            {/* Affichage de la proposition pour les questions ouvertes après soumission */}
            {answerSubmitted && currentQuestion?.type === 'open_ended' && currentQuestion?.proposition && (
              <div className="mt-4 p-3" style={{
                background: '#e8f5e8',
                border: '1px solid #4caf50',
                borderRadius: '8px',
                color: '#2e7d32'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#2e7d32' }}>💡 Proposition de réponse :</h4>
                <div 
                  className="proposition-content"
                  style={{ 
                    lineHeight: '1.6',
                    fontSize: '0.95rem'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: marked.parse(currentQuestion.proposition, {
                      breaks: true,
                      gfm: true
                    }) as string
                  }}
                />
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .proposition-content strong {
                      font-weight: 600;
                      color: #1b5e20;
                    }
                    .proposition-content em {
                      font-style: italic;
                      color: #2e7d32;
                    }
                    .proposition-content ul {
                      margin: 0.5rem 0;
                      padding-left: 1.5rem;
                    }
                    .proposition-content li {
                      margin: 0.25rem 0;
                    }
                    .proposition-content p {
                      margin: 0.5rem 0;
                    }
                    .proposition-content h1,
                    .proposition-content h2,
                    .proposition-content h3,
                    .proposition-content h4,
                    .proposition-content h5,
                    .proposition-content h6 {
                      margin: 0.75rem 0 0.5rem 0;
                      color: #1b5e20;
                      font-weight: 600;
                    }
                  `
                }} />
                <div className="mt-2" style={{ 
                  fontSize: '0.85rem', 
                  fontStyle: 'italic',
                  color: '#4caf50'
                }}>
                  💭 Comparez votre réponse avec cette proposition pour améliorer votre compréhension.
                </div>
              </div>
            )}
          </div>
        )}

        <div className="card mt-4">
          <div className="card-header">
            <h3 className="card-title">📊 Informations de session</h3>
          </div>
          <div className="card-content">
            <div className="grid grid-3">
              <div>
                <strong>Session:</strong><br />
                {session?.name || 'N/A'}
              </div>
              <div>
                <strong>Code:</strong><br />
                <code>{studentInfo?.sessionCode || 'N/A'}</code>
              </div>
              <div>
                <strong>Statut:</strong><br />
                <span className={`status-badge ${
                  session?.status === 'active' ? 'status-active' : 
                  session?.status === 'paused' ? 'status-paused' : 
                  'status-created'
                }`}>
                  {session?.status || 'Inconnu'}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Affichage des statistiques personnelles quand la session est terminée et le bouton est cliqué */}
      {session?.status === 'ended' && studentInfo && showStatistics && (
        <StudentSessionStats 
          session={session}
          questions={allQuestions}
          studentId={studentInfo.id}
          studentName={studentInfo.name}
        />
      )}
      </main>
    </div>
  );
};

export default StudentSession;
